import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { PublicCartAPI, CustomerOrdersAPI, getProductImageUrl } from '../Services/ECommerceAPI';
import './PublicLayout.css';
import '../Pages/Public/publicBoutique.css';

const CART_UPDATED_EVENT = 'cartUpdated';

/** Call this after adding/updating/removing cart items so the header cart refreshes. */
export function notifyCartUpdated() {
    window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT));
}

const PublicLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const token = sessionStorage.getItem('token');
    const user = token ? JSON.parse(sessionStorage.getItem('user') || '{}') : null;

    const [cartItems, setCartItems] = useState([]);
    const [cartOpen, setCartOpen] = useState(false);
    const [cartLoading, setCartLoading] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const cartRef = useRef(null);

    const loadCart = async () => {
        try {
            setCartLoading(true);
            let data;
            if (token) {
                data = await CustomerOrdersAPI.getCart();
            } else {
                const sessionId = sessionStorage.getItem('cartSessionId');
                if (!sessionId) {
                    setCartItems([]);
                    return;
                }
                data = await PublicCartAPI.getCart(sessionId);
            }
            const items = data?.cartItems ?? data?.CartItems ?? [];
            setCartItems(Array.isArray(items) ? items : []);
        } catch {
            setCartItems([]);
        } finally {
            setCartLoading(false);
        }
    };

    useEffect(() => {
        loadCart();
        const onCartUpdated = () => loadCart();
        window.addEventListener(CART_UPDATED_EVENT, onCartUpdated);
        return () => window.removeEventListener(CART_UPDATED_EVENT, onCartUpdated);
    }, [token]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (cartRef.current && !cartRef.current.contains(e.target)) {
                setCartOpen(false);
            }
        };
        if (cartOpen) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => document.removeEventListener('click', handleClickOutside);
    }, [cartOpen]);

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);

    const cartCount = cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const subtotal = cartItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);

    const handleLogout = () => {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        navigate('/shop');
    };

    const closeMenu = () => setMenuOpen(false);

    return (
        <div className="public-layout public-boutique">
            <header className={`public-header ${menuOpen ? 'mobile-nav-open' : ''}`}>
                <div className="header-content">
                    <Link to="/shop" className="logo" onClick={closeMenu}>
                        <h1>E-Commerce Store</h1>
                    </Link>
                    <div className="header-tools">
                        <button
                            type="button"
                            className="hamburger-btn"
                            onClick={() => setMenuOpen((o) => !o)}
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={menuOpen}
                        >
                            <span className="hamburger-bar" />
                            <span className="hamburger-bar" />
                            <span className="hamburger-bar" />
                        </button>
                        <div className="header-cart-wrap" ref={cartRef}>
                            <button
                                type="button"
                                className="header-cart-btn"
                                onClick={(e) => { e.stopPropagation(); setCartOpen((o) => !o); }}
                                aria-label="Cart"
                            >
                                <span className="header-cart-icon" aria-hidden="true">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                                    </svg>
                                </span>
                                {cartCount > 0 && (
                                    <span className="header-cart-badge">{cartCount > 99 ? '99+' : cartCount}</span>
                                )}
                            </button>
                            {cartOpen && (
                                <div className="header-cart-dropdown">
                                    <div className="mini-cart-header">
                                        <h3>Your Cart</h3>
                                        <span className="mini-cart-count">{cartCount} item{cartCount !== 1 ? 's' : ''}</span>
                                    </div>
                                    {cartLoading ? (
                                        <div className="mini-cart-loading">Loading cart...</div>
                                    ) : cartItems.length === 0 ? (
                                        <div className="mini-cart-empty">Your cart is empty. Keep shopping!</div>
                                    ) : (
                                        <>
                                            <ul className="mini-cart-list">
                                                {cartItems.map((item) => {
                                                    const product = item.product ?? item.Product;
                                                    const img = product?.images?.[0] ?? product?.Images?.[0];
                                                    const imageUrl = img?.imageUrl ?? img?.ImageUrl;
                                                    const name = product?.name ?? product?.Name ?? 'Product';
                                                    const price = item.price ?? item.Price ?? 0;
                                                    const qty = item.quantity ?? item.Quantity ?? 0;
                                                    return (
                                                        <li key={item.cartItemID ?? item.CartItemID} className="mini-cart-item">
                                                            <div className="mini-cart-item-image">
                                                                {imageUrl ? (
                                                                    <img src={getProductImageUrl(imageUrl)} alt={name} />
                                                                ) : (
                                                                    <div className="mini-cart-no-image">No image</div>
                                                                )}
                                                            </div>
                                                            <div className="mini-cart-item-info">
                                                                <span className="mini-cart-item-name">{name}</span>
                                                                <span className="mini-cart-item-qty">Qty: {qty}</span>
                                                                <span className="mini-cart-item-price">${(price * qty).toFixed(2)}</span>
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                            <div className="mini-cart-footer">
                                                <div className="mini-cart-subtotal">
                                                    <span>Subtotal</span>
                                                    <strong>${subtotal.toFixed(2)}</strong>
                                                </div>
                                                <Link to="/cart" className="mini-cart-btn view-cart-btn" onClick={() => setCartOpen(false)}>View Cart</Link>
                                                <Link to="/cart" className="mini-cart-btn checkout-btn" onClick={() => setCartOpen(false)}>Checkout</Link>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <nav className="public-nav">
                        <Link to="/shop" onClick={closeMenu}>Shop</Link>
                        {token ? (
                            <>
                                <Link to="/cart" onClick={closeMenu}>Cart</Link>
                                <Link to="/orders" onClick={closeMenu}>My Orders</Link>
                                <span className="user-name">Hello, {user?.firstName || 'User'}</span>
                                <button onClick={() => { handleLogout(); closeMenu(); }} className="logout-btn">Logout</button>
                            </>
                        ) : (
                            <>
                                <Link to="/customer/login" onClick={closeMenu}>Login</Link>
                                <Link to="/customer/register" onClick={closeMenu}>Register</Link>
                            </>
                        )}
                    </nav>
                </div>
            </header>
            <main className="public-main">
                <Outlet />
            </main>
            <footer className="public-footer">
                <p>&copy; 2024 E-Commerce Store. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default PublicLayout;
